import React, { useState } from "react";
import { Typography, Paper, IconButton, Tooltip } from "@mui/material";
import { Draggable } from "react-beautiful-dnd";
import QueueMusicIcon from "@mui/icons-material/QueueMusic";

const PictureCard = React.memo((props: any) => {
  const { pictureItem, cover, index, keyItem, draggableId, onSongPreview } = props;
  let { size, imageSize, haveNames } = props;
  if (!size) size = 150;
  if (!imageSize) imageSize = 100;
  if (haveNames === undefined) haveNames = true;

  const [hovered, setHovered] = useState(false);
  const isAlbum = Boolean(pictureItem.AlbumName && !pictureItem.SongName && !pictureItem.IsCustom);

  return (
    <Draggable key={keyItem} draggableId={draggableId} index={index}>
      {(provided, snapshot) => {
        const dragStyle = provided.draggableProps.style as React.CSSProperties | undefined;
        const transform =
          typeof dragStyle?.transform === "string"
            ? dragStyle.transform.replace(/translate\(([^,]+), ([^)]+)\)/, "translate3d($1, $2, 0)")
            : dragStyle?.transform;
        const mergedStyle: React.CSSProperties = {
          ...(dragStyle ?? {}),
          transform,
          transition: snapshot.isDragging
            ? "transform 0.1s ease, box-shadow 0.1s ease"
            : dragStyle?.transition,
          boxShadow: snapshot.isDragging ? "0 12px 28px rgba(0,0,0,0.45)" : "none",
          willChange: "transform",
        };

        return (
          <Paper
            ref={provided.innerRef}
            id="pictureCardPaper"
            {...provided.draggableProps}
            {...provided.dragHandleProps}
            style={mergedStyle}
            onMouseEnter={() => setHovered(true)}
            onMouseLeave={() => setHovered(false)}
            sx={{
              width: !haveNames ? `${imageSize}px` : "100%",
              display: "flex",
              flexDirection: "column",
              justifyContent: "center",
              alignItems: "center",
              minHeight: `${size}px`,
              height: "auto",
              backgroundColor: "var(--surface-1)",
              borderRadius: "6px",
              border: "1px solid var(--border-soft)",
              cursor: "grab",
              position: "relative",
              "&:hover": {
                border: "1px solid var(--border-mid)",
              },
            }}
          >
            <>
              {/* Song preview icon for albums */}
              {isAlbum && onSongPreview && hovered && !snapshot.isDragging && (
                <Tooltip title="View songs" placement="top">
                  <IconButton
                    size="small"
                    onClick={(e) => {
                      e.stopPropagation();
                      e.preventDefault();
                      onSongPreview(pictureItem, cover);
                    }}
                    onMouseDown={(e) => e.stopPropagation()}
                    sx={{
                      position: "absolute",
                      top: 4,
                      right: 4,
                      zIndex: 2,
                      backgroundColor: "var(--on-media-bg)",
                      color: "var(--on-media)",
                      padding: "3px",
                      "&:hover": {
                        backgroundColor: "var(--accent)",
                        color: "var(--on-accent)",
                      },
                    }}
                  >
                    <QueueMusicIcon sx={{ fontSize: 16 }} />
                  </IconButton>
                </Tooltip>
              )}
              <img
                id="pictureCardImage"
                src={cover || pictureItem.CustomImage || "/Images/DefaultAlbumCover.svg"}
                alt={pictureItem.SongName || pictureItem.AlbumName || pictureItem.Artist}
                width={imageSize}
                height={imageSize}
                style={{ objectFit: "cover" }}
                onError={(e) => {
                  const target = e.target as HTMLImageElement;
                  if (target.src !== window.location.origin + "/Images/DefaultAlbumCover.svg") {
                    target.src = "/Images/DefaultAlbumCover.svg";
                  }
                }}
              />
              {haveNames && (
                <>
                  {(pictureItem.SongName || pictureItem.AlbumName) && (
                    <Typography
                      id="pictureCardType1"
                      variant="body2"
                      sx={{
                        color: "var(--text-primary)",
                        fontSize: "12px",
                        marginTop: "4px",
                        whiteSpace: "pre-line",
                        textAlign: "center",
                        overflow: "hidden",
                        textOverflow: "ellipsis",
                        wordBreak: "break-word",
                      }}
                    >
                      {pictureItem.SongName || pictureItem.AlbumName}
                    </Typography>
                  )}
                  <Typography
                    id="pictureCardType2"
                    variant="caption"
                    sx={{
                      color: "var(--text-primary)",
                      fontSize: "12px",
                      whiteSpace: "pre-line",
                      textAlign: "center",
                      overflow: "hidden",
                      textOverflow: "ellipsis",
                    }}
                  >
                    {pictureItem.Artist}
                  </Typography>
                </>
              )}
            </>
          </Paper>
        );
      }}
    </Draggable>
  );
});

export default PictureCard;
